import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Filter, Search, MoreHorizontal, Eye, Edit, Trash, Star, CheckCircle2, Undo2, MapPin, Calendar } from 'lucide-react'
import {
  useProjectPages,
  useDeleteProjectPage,
  useToggleFeatured,
  usePublishProjectPage,
  useUnpublishProjectPage
} from '@/features/project-pages/hooks/useProjectPages'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import type { ProjectPageQueryParams, ProjectPageStatus } from '@/types/projectPages'

/**
 * Project Pages Management
 *
 * Main list view for managing marketing project pages.
 * Features: filtering, sorting, pagination, bulk actions, and quick actions.
 */
export default function ProjectPagesManagement() {
  const navigate = useNavigate()
  const [queryParams, setQueryParams] = useState<ProjectPageQueryParams>({
    page: 1,
    pageSize: 20,
    sortBy: 'created_at',
    sortDir: 'desc'
  })

  const [searchInput, setSearchInput] = useState('')

  const { data, isLoading, isError } = useProjectPages(queryParams)
  const deleteMutation = useDeleteProjectPage()
  const toggleFeaturedMutation = useToggleFeatured()
  const publishMutation = usePublishProjectPage()
  const unpublishMutation = useUnpublishProjectPage()

  const [publishingId, setPublishingId] = useState<string | null>(null)
  const [unpublishingId, setUnpublishingId] = useState<string | null>(null)

  // Handle search with debounce
  const handleSearch = () => {
    setQueryParams((prev) => ({
      ...prev,
      searchTerm: searchInput,
      page: 1 // Reset to first page on search
    }))
  }

  // Handle status filter
  const handleStatusFilter = (status: string) => {
    setQueryParams((prev) => ({
      ...prev,
      status: status === 'all' ? undefined : [status as ProjectPageStatus],
      page: 1
    }))
  }

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setQueryParams((prev) => ({ ...prev, page: newPage }))
  }

  // Handle delete
  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this project page?')) {
      deleteMutation.mutate(id)
    }
  }

  // Handle toggle featured
  const handleToggleFeatured = (id: string, currentFeatured: boolean) => {
    toggleFeaturedMutation.mutate({ id, featured: !currentFeatured })
  }

  // Handle publish now
  const handlePublishNow = async (id: string) => {
    setPublishingId(id)
    try {
      await publishMutation.mutateAsync({ id })
    } finally {
      setPublishingId(null)
    }
  }

  // Handle unpublish
  const handleUnpublish = async (id: string) => {
    setUnpublishingId(id)
    try {
      await unpublishMutation.mutateAsync(id)
    } finally {
      setUnpublishingId(null)
    }
  }

  // Get status badge variant
  const getStatusBadge = (status: ProjectPageStatus) => {
    switch (status) {
      case 'published':
        return <Badge variant="default" className="bg-green-600">Published</Badge>
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>
      case 'archived':
        return <Badge variant="outline">Archived</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Project Pages</h1>
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
          <p className="text-destructive">Failed to load project pages. Please try again.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Project Pages</h1>
          <p className="text-muted-foreground">
            Manage marketing pages for completed masonry projects
          </p>
        </div>
        <Button asChild className="w-full sm:w-auto">
          <Link to="/content/project-pages/new">
            <Plus className="mr-2 h-4 w-4" />
            New Project Page
          </Link>
        </Button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:gap-4 lg:space-y-0">
        <div className="flex-1 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by title, description, or slug..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 w-full"
            />
          </div>
          <Button onClick={handleSearch} variant="secondary" className="shrink-0">
            Search
          </Button>
        </div>

        <Select
          value={queryParams.status?.[0] || 'all'}
          onValueChange={handleStatusFilter}
        >
          <SelectTrigger className="w-full lg:w-[180px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      {data && (
        <div className="text-sm text-muted-foreground">
          Showing {((data.page - 1) * data.pageSize) + 1} to{' '}
          {Math.min(data.page * data.pageSize, data.totalCount)} of {data.totalCount} project pages
        </div>
      )}

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-muted-foreground">Loading project pages...</p>
          </div>
        ) : !data?.pages.length ? (
          <div className="text-center py-12 border rounded-lg bg-muted/20">
            <p className="text-muted-foreground">No project pages found.</p>
            <Button asChild variant="link" className="mt-2">
              <Link to="/content/project-pages/new">Create your first project page</Link>
            </Button>
          </div>
        ) : (
          data.pages.map((page) => (
            <Card key={page.id} className="overflow-hidden" onClick={() => navigate(`/content/project-pages/${page.id}/edit`)}>
              <CardContent className="p-0">
                <div className="p-4 space-y-3">
                  <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-lg line-clamp-1">{page.title}</h3>
                      <p className="text-xs text-muted-foreground font-mono">/{page.slug}</p>
                    </div>
                    {getStatusBadge(page.status)}
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                    {page.service_city && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{page.service_city}, {page.service_province}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{new Date(page.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFeatured(page.id, page.featured);
                        }}
                      >
                        <Star
                          className={`h-4 w-4 mr-1.5 ${page.featured ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                        />
                        <span className="text-xs">{page.featured ? 'Featured' : 'Feature'}</span>
                      </Button>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {page.status !== 'published' && (
                          <DropdownMenuItem
                            disabled={publishingId === page.id}
                            onClick={(event) => {
                              event.stopPropagation()
                              handlePublishNow(page.id)
                            }}
                          >
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            {publishingId === page.id ? 'Publishing...' : 'Publish Now'}
                          </DropdownMenuItem>
                        )}
                        {page.status === 'published' && (
                          <DropdownMenuItem
                            disabled={unpublishingId === page.id}
                            onClick={(event) => {
                              event.stopPropagation()
                              handleUnpublish(page.id)
                            }}
                          >
                            <Undo2 className="mr-2 h-4 w-4" />
                            {unpublishingId === page.id ? 'Unpublishing...' : 'Unpublish'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <a
                            href={`https://fixmybrick.ca/projects/${page.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Site
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/content/project-pages/${page.id}/edit`}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(page.id)}
                          className="text-destructive"
                        >
                          <Trash className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Gallery</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    <span>Loading project pages...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : !data?.pages.length ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <p className="text-muted-foreground">No project pages found.</p>
                  <Button asChild variant="link" className="mt-2">
                    <Link to="/content/project-pages/new">Create your first project page</Link>
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              data.pages.map((page) => (
                <TableRow
                  key={page.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/content/project-pages/${page.id}/edit`)}
                >
                  <TableCell className="font-medium">
                    <div>
                      <div>{page.title}</div>
                      {page.service_city && (
                        <div className="text-xs text-muted-foreground">
                          {page.service_city}, {page.service_province}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">/{page.slug}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {page.gallery ? (
                      <Link
                        to={`/media/galleries/${page.gallery_id}`}
                        className="text-sm text-primary hover:underline"
                      >
                        {page.gallery.title}
                      </Link>
                    ) : (
                      <span className="text-xs text-muted-foreground">No gallery</span>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(page.status)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFeatured(page.id, page.featured)}
                    >
                      <Star
                        className={`h-4 w-4 ${page.featured ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                      />
                    </Button>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(page.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {page.status !== 'published' && (
                          <DropdownMenuItem
                            disabled={publishingId === page.id}
                            onClick={(event) => {
                              event.stopPropagation()
                              handlePublishNow(page.id)
                            }}
                          >
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            {publishingId === page.id ? 'Publishing...' : 'Publish Now'}
                          </DropdownMenuItem>
                        )}
                        {page.status === 'published' && (
                          <DropdownMenuItem
                            disabled={unpublishingId === page.id}
                            onClick={(event) => {
                              event.stopPropagation()
                              handleUnpublish(page.id)
                            }}
                          >
                            <Undo2 className="mr-2 h-4 w-4" />
                            {unpublishingId === page.id ? 'Unpublishing...' : 'Unpublish'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <a
                            href={`https://fixmybrick.ca/projects/${page.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View on Site
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/content/project-pages/${page.id}/edit`}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(page.id)}
                          className="text-destructive"
                        >
                          <Trash className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data && data.totalCount > data.pageSize && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => handlePageChange(data.page - 1)}
            disabled={data.page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {data.page} of {Math.ceil(data.totalCount / data.pageSize)}
          </span>
          <Button
            variant="outline"
            onClick={() => handlePageChange(data.page + 1)}
            disabled={data.page >= Math.ceil(data.totalCount / data.pageSize)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
